<template>
  <b-modal
    v-model="showModal"
    size="lg"
    hide-footer
    scrollable
    @show="onModalShow"
    @hide="onModalHide"
  >
    <template #modal-header="{ close }">
      <div class="d-flex align-items-center w-100" style="min-width: 0; gap: 8px;">
        <span class="bulk-drag-handle" @mousedown="onDragStart" title="Drag to move">⠿</span>
        <span style="font-size: 1.2rem; font-weight: 600; flex: 1; min-width: 0; color: #1c4a50;">Create Invoice Charges</span>
        <button type="button" @click="close()" style="background: none; border: none; padding: 0; margin: 0; line-height: 1; font-size: 1.4rem; color: #6c757d; cursor: pointer; display: flex; align-items: center;">&times;</button>
      </div>
    </template>
    <!-- Results view -->
    <template v-if="resultsReady">
      <b-alert
        :variant="failedCount === 0 ? 'success' : failedCount === results.length ? 'danger' : 'warning'"
        show
        class="mb-3"
        :class="{ 'limeiq-success-alert': failedCount === 0 }"
      >
        {{ successCount }} of {{ results.length }} invoice charges created successfully.
        <span v-if="failedCount > 0">{{ failedCount }} failed.</span>
        <ul v-if="failedCount > 0" class="mb-0 mt-2">
          <li v-for="r in results.filter(r => !r.success)" :key="r.bdx_key" class="small">
            <strong>{{ r.bdx_key }}</strong> — {{ r.error }}
          </li>
        </ul>
      </b-alert>

      <b-table
        :items="results"
        :fields="resultsFields"
        small
        responsive
        class="mb-3"
      >
        <template #cell(status)="{ item }">
          <b-badge :variant="item.success ? 'success' : 'danger'" :class="{ 'limeiq-success-badge': item.success }">
            {{ item.success ? 'Created' : 'Failed' }}
          </b-badge>
        </template>
      </b-table>

      <div class="d-flex justify-content-end">
        <b-button variant="secondary" @click="close">Close</b-button>
      </div>
    </template>

    <!-- Duplicate confirmation view -->
    <template v-else-if="confirmingSkip">
      <div v-if="duplicateKeys.length" class="mb-3 p-3 rounded" style="background: #fdf0f0; border: 1px solid #dc3545;">
        <div class="d-flex align-items-center mb-2">
          <strong style="color: #dc3545;">⚠ Skipped — Already Exist ({{ duplicateKeys.length }})</strong>
        </div>
        <p class="small mb-2" style="color: #dc3545;">An invoice charge for this reporting period already exists for these keys. Creating a duplicate is not allowed.</p>
        <div style="max-height: 120px; overflow-y: auto;">
          <div v-for="k in duplicateKeys" :key="k" class="small py-1 px-2 mb-1 rounded" style="background: #fff; border: 1px solid #dc3545; color: #dc3545; word-break: break-all;">
            {{ k }}
          </div>
        </div>
      </div>

      <div v-if="noDedupeKeys.length" class="mb-3 p-3 rounded" style="background: #fff8e1; border: 1px solid #f59e0b;">
        <div class="d-flex align-items-center mb-2">
          <strong style="color: #b45309;">⚠ Skipped — No Dedupe Submission ({{ noDedupeKeys.length }})</strong>
        </div>
        <p class="small mb-2" style="color: #b45309;">No dedupe submission exists for this reporting period for these keys.</p>
        <div style="max-height: 120px; overflow-y: auto;">
          <div v-for="k in noDedupeKeys" :key="k" class="small py-1 px-2 mb-1 rounded" style="background: #fff; border: 1px solid #f59e0b; color: #b45309; word-break: break-all;">
            {{ k }}
          </div>
        </div>
      </div>

      <template v-if="keysToCreate.length">
        <div class="mb-3 p-3 rounded" style="background: #f0fdf4; border: 1px solid #198754;">
          <div class="d-flex align-items-center mb-2">
            <strong style="color: #198754;">✓ Will Be Created ({{ keysToCreate.length }})</strong>
          </div>
          <div style="max-height: 120px; overflow-y: auto;">
            <div v-for="item in keysToCreate" :key="item.umr_rc_cc_sn" class="small py-1 px-2 mb-1 rounded" style="background: #fff; border: 1px solid #198754; color: #198754; word-break: break-all;">
              {{ item.umr_rc_cc_sn }}
            </div>
          </div>
        </div>
        <p class="text-muted small mb-3">Do you wish to continue and create charges for the {{ keysToCreate.length }} key{{ keysToCreate.length !== 1 ? 's' : '' }} above?</p>
      </template>
      <b-alert v-else variant="danger" show class="mb-3">
        Nothing to create — all selected keys are either duplicates or have no dedupe submission for this reporting period.
      </b-alert>

      <div class="d-flex justify-content-between">
        <b-button v-if="keysToCreate.length" class="limeiq-primary-btn" @click="proceedWithCreate(keysToCreate)">Continue for {{ keysToCreate.length }} Key{{ keysToCreate.length !== 1 ? 's' : '' }}</b-button>
        <b-button variant="secondary" @click="confirmingSkip = false">Go Back</b-button>
      </div>
    </template>

    <!-- Processing view -->
    <template v-else-if="processing">
      <div class="text-center py-4">
        <b-spinner class="mb-3" />
        <p class="mb-1">Processing {{ processedCount }} of {{ selectedItems.length }} keys...</p>
        <p class="text-muted small">{{ currentlyProcessing }}</p>
      </div>
    </template>

    <!-- Form view -->
    <template v-else>
      <div class="mb-3">
        <div class="d-flex align-items-center justify-content-between mb-1">
          <strong class="small text-muted text-uppercase">Selected Keys ({{ selectedItems.length }})</strong>
        </div>
        <div style="max-height: 130px; overflow-y: auto; border: 1px solid #dee2e6; border-radius: 4px;">
          <table style="width: 100%; border-collapse: collapse;">
            <tr
              v-for="(item, index) in selectedItems"
              :key="item.id"
              :style="{ background: index % 2 === 0 ? '#f8f9fa' : '#fff' }"
            >
              <td class="small text-muted px-2" style="width: 36px; padding: 4px 8px; white-space: nowrap;">{{ index + 1 }}</td>
              <td class="small px-2" style="padding: 4px 8px; word-break: break-all; font-family: monospace;">{{ item.umr_rc_cc_sn || item.id }}</td>
            </tr>
          </table>
        </div>
      </div>

      <hr class="my-3" />

      <b-form @submit.prevent="handleSubmit">
        <b-row>
          <b-col md="6">
            <b-form-group label="Invoice Charge Date *">
              <b-form-input v-model="form.created_on" type="date" required />
            </b-form-group>
          </b-col>
          <b-col md="6">
            <b-form-group label="Reporting Period *">
              <b-form-select v-model="form.reporting_period" :disabled="loadingPeriods" required>
                <template #first>
                  <b-form-select-option value="">{{ loadingPeriods ? 'Loading...' : 'Select Period' }}</b-form-select-option>
                </template>
                <b-form-select-option
                  v-for="p in availablePeriods"
                  :key="p.value"
                  :value="p.value"
                >{{ p.label }}</b-form-select-option>
              </b-form-select>
            </b-form-group>
          </b-col>
        </b-row>

        <b-row>
          <b-col md="6">
            <b-form-group label="Amount *">
              <b-form-input
                v-model="form.amount"
                type="number"
                step="0.01"
                min="0"
                required
                placeholder="Enter amount"
              />
            </b-form-group>
          </b-col>
          <b-col md="6">
            <b-form-group label="Currency Type">
              <b-form-select v-model="form.currency_type">
                <template #first>
                  <b-form-select-option value="">Select Currency</b-form-select-option>
                </template>
                <b-form-select-option
                  v-for="currency in BDX_CURRENCY_TYPES"
                  :key="currency"
                  :value="currency"
                >{{ currency }}</b-form-select-option>
              </b-form-select>
              <small v-if="mixedCurrencies" class="text-warning">
                Selected keys have different currencies. The chosen currency will be applied to all.
              </small>
            </b-form-group>
          </b-col>
        </b-row>

        <b-row>
          <b-col md="6">
            <b-form-group label="Source">
              <b-form-input value="Manual" disabled />
            </b-form-group>
          </b-col>
        </b-row>

        <b-row>
          <b-col md="12">
            <b-form-group label="Reason for Adjustment *">
              <b-form-textarea
                v-model="form.reason_for_adjustment"
                rows="2"
                placeholder="Required — describe why these charges are being created"
                required
              />
            </b-form-group>
          </b-col>
        </b-row>

        <div v-if="formError" class="alert alert-danger py-2 small">{{ formError }}</div>

        <div class="d-flex justify-content-between mt-3">
          <b-button type="submit" class="limeiq-primary-btn" :disabled="!selectedItems.length">
            Create for {{ selectedItems.length }} Key{{ selectedItems.length !== 1 ? 's' : '' }}
          </b-button>
          <b-button variant="secondary" @click="close">Cancel</b-button>
        </div>
      </b-form>
    </template>
  </b-modal>
</template>

<script>
import Vue from 'vue'
import { defineComponent, ref, computed } from '@vue/composition-api'
import { InvoiceChargeService } from '@services'
import { BDX_CURRENCY_TYPES } from '@constants'

const today = () => new Date().toISOString().slice(0, 10)

const EMPTY_FORM = () => ({
  created_on: today(),
  reporting_period: '',
  amount: '',
  currency_type: '',
  source: 'Manual',
  reason_for_adjustment: ''
})

const RESULTS_FIELDS = [
  { key: 'bdx_key', label: 'BDX Key' },
  { key: 'status', label: 'Status' },
]

export default defineComponent({
  name: 'BulkInvoiceChargeModal',
  props: {
    value: { type: Boolean, default: false },
    selectedItems: { type: Array, default: () => [] }
  },
  emits: ['input'],
  setup(props, { emit }) {
    const showModal = computed({
      get: () => props.value,
      set: (v) => emit('input', v)
    })

    const form = ref(EMPTY_FORM())
    const formError = ref('')
    const availablePeriods = ref([])
    const loadingPeriods = ref(false)
    const processing = ref(false)
    const resultsReady = ref(false)
    const results = ref([])
    const processedCount = ref(0)
    const currentlyProcessing = ref('')
    const confirmingSkip = ref(false)
    const duplicateKeys = ref([])
    const noDedupeKeys = ref([])
    const keysToCreate = computed(() =>
      props.selectedItems.filter(item => {
        const key = item.umr_rc_cc_sn || ''
        return !duplicateKeys.value.includes(key) && !noDedupeKeys.value.includes(key)
      })
    )

    const resultsFields = RESULTS_FIELDS

    const successCount = computed(() => results.value.filter(r => r.success).length)
    const failedCount = computed(() => results.value.filter(r => !r.success).length)

    const mixedCurrencies = computed(() => {
      const currencies = [...new Set(props.selectedItems.map(i => i.currency_type).filter(Boolean))]
      return currencies.length > 1
    })

    const onModalShow = async () => {
      form.value = EMPTY_FORM()
      formError.value = ''
      processing.value = false
      resultsReady.value = false
      results.value = []
      processedCount.value = 0
      currentlyProcessing.value = ''
      confirmingSkip.value = false
      duplicateKeys.value = []
      noDedupeKeys.value = []
      dragOffset.value = { x: 0, y: 0 }
      const dialog = getDialog()
      if (dialog) dialog.style.transform = ''

      // Pre-fill currency if all selected items share the same one
      const currencies = [...new Set(props.selectedItems.map(i => i.currency_type).filter(Boolean))]
      if (currencies.length === 1) {
        form.value.currency_type = currencies[0]
      }

      // Load available periods: intersection of all selected keys' periods
      loadingPeriods.value = true
      availablePeriods.value = []
      try {
        const keys = props.selectedItems.map(i => i.umr_rc_cc_sn).filter(Boolean)
        if (keys.length) {
          const responses = await Promise.all(keys.map(k => InvoiceChargeService.availablePeriods(k).catch(() => ({ data: { periods: [] } }))))
          const seen = new Set()
          const union = []
          for (const r of responses) {
            for (const p of (r.data.periods || [])) {
              if (!seen.has(p.value)) {
                seen.add(p.value)
                union.push(p)
              }
            }
          }
          union.sort((a, b) => b.value.localeCompare(a.value))
          availablePeriods.value = union
        }
      } finally {
        loadingPeriods.value = false
      }
    }

    const onModalHide = () => {
      if (results.value.some(r => r.success)) {
        Vue.prototype.$emitter.emit('invoice-charge:success')
      }
      form.value = EMPTY_FORM()
      formError.value = ''
      processing.value = false
      resultsReady.value = false
      results.value = []
      confirmingSkip.value = false
      duplicateKeys.value = []
      noDedupeKeys.value = []
      availablePeriods.value = []
      loadingPeriods.value = false
    }

    const close = () => { showModal.value = false }

    const dragOffset = ref({ x: 0, y: 0 })
    const dragStart = ref({ mouseX: 0, mouseY: 0 })
    const isDragging = ref(false)

    const getDialog = () => document.querySelector('.modal.show .modal-dialog')

    const onDragStart = (e) => {
      e.preventDefault()
      const handleEl = e.currentTarget
      isDragging.value = true
      dragStart.value = { mouseX: e.clientX - dragOffset.value.x, mouseY: e.clientY - dragOffset.value.y }
      handleEl.style.cursor = 'grabbing'
      document.body.style.userSelect = 'none'

      const onMove = (ev) => {
        if (!isDragging.value) return
        dragOffset.value.x = ev.clientX - dragStart.value.mouseX
        dragOffset.value.y = ev.clientY - dragStart.value.mouseY
        const dialog = getDialog()
        if (dialog) {
          dialog.style.transform = `translate(${dragOffset.value.x}px, ${dragOffset.value.y}px)`
        }
      }

      const onUp = () => {
        isDragging.value = false
        handleEl.style.cursor = 'grab'
        document.body.style.userSelect = ''
        document.removeEventListener('mousemove', onMove)
        document.removeEventListener('mouseup', onUp)
      }

      document.addEventListener('mousemove', onMove)
      document.addEventListener('mouseup', onUp)
    }

    const handleSubmit = async () => {
      if (!props.selectedItems.length) return
      formError.value = ''

      const allKeys = props.selectedItems.map(i => i.umr_rc_cc_sn || '')
      try {
        const reportingPeriodDate = form.value.reporting_period || ''
        const res = await InvoiceChargeService.checkDuplicates(allKeys, reportingPeriodDate)
        duplicateKeys.value = res.data.duplicates || []
        noDedupeKeys.value = res.data.no_dedupe || []
      } catch {
        duplicateKeys.value = []
        noDedupeKeys.value = []
      }

      if (duplicateKeys.value.length || noDedupeKeys.value.length) {
        confirmingSkip.value = true
        return
      }

      await proceedWithCreate(props.selectedItems.slice())
    }

    const proceedWithCreate = async (itemsToProcess) => {
      confirmingSkip.value = false
      processing.value = true
      resultsReady.value = false
      results.value = []
      processedCount.value = 0

      const f = form.value

      for (const item of itemsToProcess) {
        const bdxKey = item.umr_rc_cc_sn || ''
        currentlyProcessing.value = bdxKey
        const payload = {
          bdx_key: bdxKey,
          charge_type: 'Invoice Charge',
          tpa: (item.tpa && item.tpa.TPA) || null,
          reporting_period: f.reporting_period || null,
          amount: f.amount || null,
          currency_type: f.currency_type || item.currency_type || null,
          source: f.source || null,
          created_on: f.created_on ? `${f.created_on}T00:00:00` : null,
          reason_for_adjustment: f.reason_for_adjustment
        }

        try {
          await InvoiceChargeService.create(payload)
          results.value.push({ bdx_key: bdxKey, success: true, error: '' })
        } catch (err) {
          const errData = err && err.response && err.response.data
          let msg = 'Failed to create'
          if (errData) {
            if (typeof errData === 'string') msg = errData
            else if (Array.isArray(errData)) msg = errData.join(' ')
            else if (errData.error) msg = Array.isArray(errData.error) ? errData.error.join(' ') : errData.error
            else if (errData.message) msg = errData.message
            else if (errData.non_field_errors) msg = Array.isArray(errData.non_field_errors) ? errData.non_field_errors.join(' ') : errData.non_field_errors
            else msg = Object.values(errData).flat().join(' ')
          }
          results.value.push({ bdx_key: bdxKey, success: false, error: msg })
        }

        processedCount.value += 1
      }

      processing.value = false
      resultsReady.value = true

      if (results.value.some(r => r.success)) {
        Vue.prototype.$emitter.emit('invoice-charge:success')
      }
    }

    return {
      showModal,
      form,
      formError,
      availablePeriods,
      loadingPeriods,
      processing,
      resultsReady,
      results,
      resultsFields,
      processedCount,
      currentlyProcessing,
      successCount,
      failedCount,
      mixedCurrencies,
      confirmingSkip,
      duplicateKeys,
      noDedupeKeys,
      keysToCreate,
      onModalShow,
      onModalHide,
      close,
      handleSubmit,
      proceedWithCreate,
      onDragStart,
      BDX_CURRENCY_TYPES
    }
  }
})
</script>

<style scoped>
>>> .modal-body {
  padding-top: 8px;
}
>>> .modal-header {
  padding-bottom: 4px;
}
>>> .modal-header .bulk-drag-handle {
  cursor: grab;
  color: #aaa;
  font-size: 1.2rem;
  line-height: 1;
  padding: 0 4px;
  flex-shrink: 0;
}
>>> .modal-header .bulk-drag-handle:active {
  cursor: grabbing;
}

.limeiq-primary-btn {
  background-color: #1c4a50 !important;
  border-color: #1c4a50 !important;
  color: #fff !important;
}
.limeiq-primary-btn:hover:not(:disabled) {
  background-color: #163c41 !important;
  border-color: #163c41 !important;
}

>>> .limeiq-success-alert {
  background-color: #1c4a50 !important;
  border-color: #1c4a50 !important;
  color: #fff !important;
}
>>> .limeiq-success-badge {
  background-color: #1c4a50 !important;
  color: #fff !important;
}
</style>
